import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface ShortcutHandlers {
  onCommandPalette?: () => void;
  onShowShortcuts?: () => void;
  onOpenHelp?: () => void;
}

export default function useKeyboardShortcuts(handlers: ShortcutHandlers) {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') {
        // Allow Escape, Cmd+K, and F1 even in inputs
        if (e.key !== 'Escape' && !(e.key === 'k' && (e.metaKey || e.ctrlKey)) && e.key !== 'F1') {
          return;
        }
      }

      // Cmd+K / Ctrl+K: Command palette
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handlers.onCommandPalette?.();
        return;
      }

      // F1: Open help
      if (e.key === 'F1') {
        e.preventDefault();
        handlers.onOpenHelp?.();
        return;
      }

      // Cmd+/ or Ctrl+/: Open help
      if (e.key === '/' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        handlers.onOpenHelp?.();
        return;
      }

      // Cmd+N / Ctrl+N: New proposal (only when not in input)
      if (e.key === 'n' && (e.metaKey || e.ctrlKey) && target.tagName !== 'INPUT') {
        e.preventDefault();
        navigate('/proposals');
        return;
      }

      // ?: Show shortcuts help (when not in input)
      if (e.key === '?' && target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') {
        e.preventDefault();
        handlers.onShowShortcuts?.();
        return;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [navigate, handlers]);
}
